import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Briefcase } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const EmployeeTypes = () => {
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [filteredEmployeeTypes, setFilteredEmployeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    employeetypeid: null,
    typecode: '',
    typename: '',
    description: '',
    isactive: true
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchEmployeeTypes();
  }, []);

  useEffect(() => {
    filterEmployeeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, employeeTypes]);

  const fetchEmployeeTypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/EmployeeTypes.php');
      
      if (response.data.success) {
        setEmployeeTypes(response.data.data || []);
      } else {
        alert('Failed to fetch employee types: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching employee types:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching employee types. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterEmployeeTypes = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployeeTypes(employeeTypes);
    } else {
      const filtered = employeeTypes.filter(type =>
        type.typename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.typecode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (type.description && type.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredEmployeeTypes(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.typecode.trim()) {
      newErrors.typecode = 'Type code is required';
    } else if (formData.typecode.length > 20) {
      newErrors.typecode = 'Type code must be 20 characters or less';
    } else if (!/^[A-Z0-9_]+$/.test(formData.typecode)) {
      newErrors.typecode = 'Type code must contain only uppercase letters, numbers, and underscores';
    }
    
    if (!formData.typename.trim()) {
      newErrors.typename = 'Type name is required';
    } else if (formData.typename.length > 50) {
      newErrors.typename = 'Type name must be 50 characters or less';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/EmployeeTypes.php', {
        ...formData,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Employee type updated successfully!' : 'Employee type created successfully!');
        setShowModal(false);
        resetForm();
        fetchEmployeeTypes();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving employee type:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error saving employee type. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (employeeType) => {
    setFormData({
      employeetypeid: employeeType.employeetypeid,
      typecode: employeeType.typecode,
      typename: employeeType.typename,
      description: employeeType.description || '',
      isactive: employeeType.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id, typename) => {
    if (!window.confirm(`Are you sure you want to delete "${typename}"?`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/EmployeeTypes.php', {
        data: { employeetypeid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Employee type deleted successfully!');
        fetchEmployeeTypes();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting employee type:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error deleting employee type. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employeetypeid: null,
      typecode: '',
      typename: '',
      description: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleTypeCodeChange = (e) => {
    // Auto-uppercase and remove spaces
    const value = e.target.value.toUpperCase().replace(/\s/g, '_');
    setFormData({ ...formData, typecode: value });
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Employee Types Master</h2>
          <p className="subtitle">Manage employee type classifications</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Employee Type
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by type code, name, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredEmployeeTypes.length} {filteredEmployeeTypes.length === 1 ? 'type' : 'types'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading employee types...</div>
      ) : filteredEmployeeTypes.length === 0 ? (
        <div className="empty-state">
          <Briefcase size={48} />
          <p>No employee types found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
          {!searchTerm && <button onClick={openAddModal}>Add your first employee type</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type Code</th>
                <th>Type Name</th>
                <th>Description</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployeeTypes.map((type) => (
                <tr key={type.employeetypeid}>
                  <td className="font-mono">{type.typecode}</td>
                  <td className="font-semibold">{type.typename}</td>
                  <td className="description-cell">{type.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${type.isactive ? 'active' : 'inactive'}`}>
                      {type.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(type)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(type.employeetypeid, type.typename)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Employee Type' : 'Add New Employee Type'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="typecode">
                  Type Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="typecode"
                  value={formData.typecode}
                  onChange={handleTypeCodeChange}
                  maxLength="20"
                  placeholder="e.g., PERM, TEMP, CONTRACT"
                  className={errors.typecode ? 'error' : ''}
                  disabled={editMode} // Prevent editing code once created
                  autoFocus
                />
                {errors.typecode && (
                  <span className="error-message">{errors.typecode}</span>
                )}
                <span className="field-hint">Uppercase letters, numbers, and underscores only</span>
              </div>

              <div className="form-group">
                <label htmlFor="typename">
                  Type Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="typename"
                  value={formData.typename}
                  onChange={(e) => setFormData({ ...formData, typename: e.target.value })}
                  maxLength="50"
                  placeholder="e.g., Permanent, Temporary, Contract"
                  className={errors.typename ? 'error' : ''}
                />
                {errors.typename && (
                  <span className="error-message">{errors.typename}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  maxLength="500"
                  rows="3"
                  placeholder="Optional description of this employee type"
                  className={errors.description ? 'error' : ''}
                />
                {errors.description && (
                  <span className="error-message">{errors.description}</span>
                )}
                <span className="field-hint">
                  {formData.description.length}/500 characters
                </span>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTypes;